import { addWeeks, endOfWeek, format, startOfWeek } from "date-fns";
import { CustomEventType, WeekOption } from "../../utils/types";
import { pt } from "date-fns/locale";

export const createWeekCopyItem = (originalEvent: CustomEventType, targetUserId: string, selectedWeek: string) => {
    const originalEventStart = new Date(originalEvent.start);
    const originalEventEnd = new Date(originalEvent.end);
    
    // selectedWeek is already a Monday ISO string from generateWeekOptions
    // So we don't need to call startOfWeek again - that was causing the bug!
    const targetWeekMonday = new Date(selectedWeek);

    // Calculate target date preserving day of week and time
    const originalDayOfWeek = originalEventStart.getDay();
    const daysFromMonday = originalDayOfWeek === 0 ? 6 : originalDayOfWeek - 1; // Convert Sunday to 6, others subtract 1

    const targetStartDateTime = new Date(targetWeekMonday);
    targetStartDateTime.setDate(targetStartDateTime.getDate() + daysFromMonday);
    targetStartDateTime.setHours(originalEventStart.getHours(), originalEventStart.getMinutes(), originalEventStart.getSeconds());

    const targetEndDateTime = new Date(targetStartDateTime);
    const eventDuration = originalEventEnd.getTime() - originalEventStart.getTime();
    targetEndDateTime.setTime(targetStartDateTime.getTime() + eventDuration);

    return {
        clinic_id: originalEvent.clinicId,
        start_date: targetStartDateTime,
        end_date: targetEndDateTime,
        user: targetUserId,
        services: {
            create:
                originalEvent.services?.map((service) => ({
                    schedules_id: "+",
                    services_id: { id: service },
                })) || [],
            update: [],
        },
        notes: originalEvent.notes,
        room: originalEvent.room,
    };
};

export const createDayCopyItems = (
    originalEvent: CustomEventType,
    targetUserId: string,
    selectedDays: Array<number | "same">,
    selectedResource: number | null
) => {
    const selectedDaysToUse = selectedDays.length === 0 ? ["same"] : selectedDays;

    return selectedDaysToUse.map((selectedDay) => {
        let targetStartDate = new Date(originalEvent.start);
        let targetEndDate = new Date(originalEvent.end);

        if (selectedDay !== "same" && typeof selectedDay === "number") {
            const currentDayOfWeek = targetStartDate.getDay();
            const daysToAdd = (selectedDay - currentDayOfWeek + 7) % 7;
            targetStartDate = new Date(targetStartDate.setDate(targetStartDate.getDate() + daysToAdd));
            targetEndDate = new Date(targetEndDate.setDate(targetEndDate.getDate() + daysToAdd));
        }

        return {
            clinic_id: originalEvent.clinicId,
            start_date: targetStartDate,
            end_date: targetEndDate,
            user: targetUserId,
            services: {
                create:
                    originalEvent.services?.map((service) => ({
                        schedules_id: "+",
                        services_id: { id: service },
                    })) || [],
                update: [],
            },
            notes: originalEvent.notes,
            room: selectedResource || originalEvent.room,
        };
    });
};

// Time off copy helpers
export const createWeekCopyItemTimeOff = (originalEvent: CustomEventType, targetUserId: string, selectedWeek: string) => {
    const originalEventStart = new Date(originalEvent.start);
    const originalEventEnd = new Date(originalEvent.end);

    const targetWeekMonday = new Date(selectedWeek);

    const originalDayOfWeek = originalEventStart.getDay();
    const daysFromMonday = originalDayOfWeek === 0 ? 6 : originalDayOfWeek - 1;

    const targetStartDateTime = new Date(targetWeekMonday);
    targetStartDateTime.setDate(targetStartDateTime.getDate() + daysFromMonday);
    targetStartDateTime.setHours(
        originalEventStart.getHours(),
        originalEventStart.getMinutes(),
        originalEventStart.getSeconds()
    );

    const targetEndDateTime = new Date(targetStartDateTime);
    const eventDuration = originalEventEnd.getTime() - originalEventStart.getTime();
    targetEndDateTime.setTime(targetStartDateTime.getTime() + eventDuration);

    return {
        clinic_id: originalEvent.clinicId,
        start_date: targetStartDateTime,
        end_date: targetEndDateTime,
        user: targetUserId,
        type: originalEvent.time_off_type,
        notes: originalEvent.notes,
    };
};

export const createDayCopyItemsTimeOff = (
    originalEvent: CustomEventType,
    targetUserId: string,
    selectedDays: Array<number | "same">
) => {
    const selectedDaysToUse = selectedDays.length === 0 ? ["same"] : selectedDays;

    return selectedDaysToUse.map((selectedDay) => {
        let targetStartDate = new Date(originalEvent.start);
        let targetEndDate = new Date(originalEvent.end);

        if (selectedDay !== "same" && typeof selectedDay === "number") {
            const currentDayOfWeek = targetStartDate.getDay();
            const daysToAdd = (selectedDay - currentDayOfWeek + 7) % 7;
            targetStartDate = new Date(targetStartDate.setDate(targetStartDate.getDate() + daysToAdd));
            targetEndDate = new Date(targetEndDate.setDate(targetEndDate.getDate() + daysToAdd));
        }

        return {
            clinic_id: originalEvent.clinicId,
            start_date: targetStartDate,
            end_date: targetEndDate,
            user: targetUserId,
            type: originalEvent.time_off_type,
            notes: originalEvent.notes,
        };
    });
};


// Generate week options for the 4 weeks before and 24 weeks (~6 months) after the current calendar view.
// The current week is included as a non-selectable marker so the user can see where they are in the list.
export const generateWeekOptions = (currentDate: Date): Array<WeekOption> => {
    const weekOptions: Array<WeekOption> = [];
    const currentWeekStart = startOfWeek(currentDate, { weekStartsOn: 1 });

    for (let weekOffset = -4; weekOffset <= 24; weekOffset++) {
        const isCurrentWeek = weekOffset === 0;

        const weekStart = addWeeks(currentWeekStart, weekOffset);
        const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });

        const startFormatted = format(weekStart, "d", { locale: pt });
        const endFormatted = format(weekEnd, "d MMMM, yyyy", { locale: pt });

        weekOptions.push({
            value: weekStart.toISOString(),
            label: isCurrentWeek
                ? `Semana atual (${startFormatted}-${endFormatted})`
                : `Semana de ${startFormatted}-${endFormatted}`,
            startDate: weekStart,
            endDate: weekEnd,
            isCurrentWeek,
        });
    }

    return weekOptions;
};
